import { useState, useCallback } from 'react'

export interface Answer {
    value: string
    label: string
    next?: string
    success?: {
        title: string
        message: string
        cta?: {
            label: string
            action: string
        }
    }
    failure?: {
        title: string
        message: string
    }
}

export interface FormStep {
    id: string
    type: string
    heading: string
    question: string
    answers: Answer[]
}

export interface FormFlow {
    flow: string
    title: string
    subtitle: string
    steps: FormStep[]
}

interface UseFormStepsReturn {
    currentStep: FormStep | null
    stepHistory: string[]
    flowData: FormFlow | null
    goToStep: (stepId: string) => void
    goNext: (answerValue: string) => void
    goBack: () => void
    reset: () => void
    isFirstStep: boolean
    isLastStep: boolean
    getAnswerByValue: (stepId: string, value: string) => Answer | undefined
}

export const useFormSteps = (initialFlow: FormFlow): UseFormStepsReturn => {
    const [flowData] = useState<FormFlow>(initialFlow)
    const [stepHistory, setStepHistory] = useState<string[]>([initialFlow.steps[0].id])

    const currentStepId = stepHistory[stepHistory.length - 1]
    const currentStep = flowData.steps.find((step: FormStep) => step.id === currentStepId) || null

    const goToStep = useCallback(
        (stepId: string) => {
            const step = flowData.steps.find((s: FormStep) => s.id === stepId)
            if (step) {
                setStepHistory((prev) => [...prev, stepId])
            }
        },
        [flowData.steps]
    )

    const goNext = useCallback(
        (answerValue: string) => {
            if (!currentStep) return

            const answer = currentStep.answers.find((a: Answer) => a.value === answerValue)
            if (!answer) return

            if (answer.next) {
                goToStep(answer.next)
            } else if (answer.success) {
                console.log('Success path:', answer.success)
            } else if (answer.failure) {
                console.log('Failure path:', answer.failure)
            }
        },
        [currentStep, goToStep]
    )

    const goBack = useCallback(() => {
        setStepHistory((prev) => {
            if (prev.length > 1) {
                return prev.slice(0, -1)
            }
            return prev
        })
    }, [])

    const reset = useCallback(() => {
        setStepHistory([flowData.steps[0].id])
    }, [flowData])

    const getAnswerByValue = useCallback(
        (stepId: string, value: string) => {
            const step = flowData.steps.find((s: FormStep) => s.id === stepId)
            return step?.answers.find((a: Answer) => a.value === value)
        },
        [flowData]
    )

    return {
        currentStep,
        stepHistory,
        flowData,
        goToStep,
        goNext,
        goBack,
        reset,
        isFirstStep: stepHistory.length === 1,
        isLastStep: currentStep?.answers.some((a: Answer) => a.success || a.failure) ?? false,
        getAnswerByValue,
    }
}
